/**
 * AMBIENT LOOPS — the shared policy behind the ~10s ash drifts (the landing's
 * hero band and its closing band, the auth screens' art pane).
 *
 * The rule everywhere: the POSTER is the picture. The video is an enhancement
 * layered over it and is only ever started when all of these hold —
 *
 *   • the visitor has not asked for reduced motion,
 *   • the connection is not metered (`saveData`) or 2G,
 *   • the tab is visible (a hidden tab decodes nothing).
 *
 * Nothing here filters or animates on scroll: the element is a plain
 * GPU-composited video under static content, so the page stays at 60fps.
 */

/** `prefers-reduced-motion: reduce` — treated as "no video at all". */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Data Saver on, or a 2G-class connection — half a megabyte of ambience is
 *  not what that visitor is paying for. `navigator.connection` is Chromium-only;
 *  everywhere else this is simply false. */
export function isMeteredConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  const c = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!c) return false;
  if (c.saveData) return true;
  return c.effectiveType === '2g' || c.effectiveType === 'slow-2g';
}

/** Should this visitor get moving pictures at all? */
export function ambientMotionAllowed(): boolean {
  return !prefersReducedMotion() && !isMeteredConnection();
}

/** The `<source>` order a loop is offered in: WebM first (smaller, and what
 *  Chromium/Firefox prefer), MP4 as the universal fallback Safari needs. */
export interface AmbientSources {
  poster: string;
  webm: string;
  mp4: string;
}

/**
 * Start playback, swallowing both ways a browser can say no: a rejected
 * promise (autoplay policy) and jsdom/older engines, where `play()` returns
 * undefined rather than a promise. The poster still is the fallback either way.
 */
export function safePlay(video: HTMLVideoElement): void {
  try {
    const p = video.play() as Promise<void> | undefined;
    void p?.catch?.(() => { /* refused — the poster stands */ });
  } catch { /* refused synchronously — same outcome */ }
}

/** ~10s seamless ping-pong ash drifts (no audio track in either file), served
 *  straight from public/ — never imported, so nothing reaches the bundle. */
export const LOOPS: Record<'hero' | 'auth' | 'finale', AmbientSources> = {
  hero: {
    poster: '/landing/loops/hero-poster.webp',
    webm: '/landing/loops/hero-loop.webm',
    mp4: '/landing/loops/hero-loop.mp4',
  },
  auth: {
    poster: '/landing/loops/auth-poster.webp',
    webm: '/landing/loops/auth-loop.webm',
    mp4: '/landing/loops/auth-loop.mp4',
  },
  // the landing's CLOSING band (`<section id="play">`): the dusk Roman road,
  // drifting ash and the walking figures the owner asked to see move.
  finale: {
    poster: '/landing/loops/finale-poster.webp',
    webm: '/landing/loops/finale-loop.webm',
    mp4: '/landing/loops/finale-loop.mp4',
  },
};

/**
 * Pause the loop whenever the tab goes away and resume when it comes back.
 * Returns the teardown. Safe to call with a video that is not playing.
 */
export function bindVisibilityPause(video: HTMLVideoElement, doc: Document = document): () => void {
  const onChange = () => {
    if (doc.hidden) {
      video.pause();
    } else {
      safePlay(video);
    }
  };
  doc.addEventListener('visibilitychange', onChange);
  return () => doc.removeEventListener('visibilitychange', onChange);
}

/**
 * Build the ambient <video> for a host element. Returns null when motion is
 * not allowed (the caller's poster/still is then the whole picture).
 *
 * Pass `{ poster: false }` when the caller already paints the still itself.
 *
 * The element is `aria-hidden` and not focusable: it carries no information,
 * only atmosphere.
 */
export function createAmbientVideo(
  src: AmbientSources,
  doc: Document = document,
  opts: { poster?: boolean } = {},
): HTMLVideoElement | null {
  if (!ambientMotionAllowed()) return null;
  const v = doc.createElement('video');
  v.setAttribute('data-ambient-loop', '');
  v.autoplay = true;
  v.muted = true;
  v.defaultMuted = true;
  v.loop = true;
  v.playsInline = true;
  v.setAttribute('playsinline', '');
  v.setAttribute('muted', '');
  v.preload = 'auto';
  // `poster: false` when something underneath already paints the still — two
  // different crops of the same scene cross-fading is worse than no poster.
  if (opts.poster !== false) v.poster = src.poster;
  v.setAttribute('aria-hidden', 'true');
  v.tabIndex = -1;
  for (const [url, type] of [[src.webm, 'video/webm'], [src.mp4, 'video/mp4']] as const) {
    const s = doc.createElement('source');
    s.src = url;
    s.type = type;
    v.appendChild(s);
  }
  return v;
}
